import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { getJeepRallyMediaUrl } from "../../../../../api/jeep-rally/api";
import { useGetTypeById } from "../../../../../api/jeep-rally/types/types.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import {
  getTypeApiErrorMessage,
  normalizeType,
} from "../utils/type.utils";

const DetailRow = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{value || "-"}</div>
  </div>
);

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const TypeViewPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();
  const locationType = history.location.state?.type || null;

  const {
    data: typeData,
    isLoading,
    isError,
    error,
  } = useGetTypeById(id);

  const type = normalizeType(typeData) || locationType;
  const imageUrl = getJeepRallyMediaUrl(type?.image);
  const isActive = type?.status === "active";

  const goBack = () => history.push("/jeep-rally/types");
  const goEdit = () => {
    if (!id) return;
    history.push(`/jeep-rally/types/edit/${id}`, { type });
  };

  if (isLoading && !locationType) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isError && !locationType) || !type) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getTypeApiErrorMessage(
                error,
                "Type not found. Please open it from the Types list."
              )}
            </Alert>
            <Button variant="outline-secondary" className="mt-3" onClick={goBack}>
              Back to types
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to types list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">View Type</h4>
          </div>
          {canManage ? (
            <Button variant="primary" size="sm" onClick={goEdit}>
              Edit
            </Button>
          ) : null}
        </div>

        <div className="card-body">
          <Card className="shadow-none mb-0">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div className="d-flex gap-3 align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center overflow-hidden border rounded bg-light"
                    style={{ width: 96, height: 72, flexShrink: 0 }}
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={type.name || "Type"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span className="text-muted small">No image</span>
                    )}
                  </div>
                  <div>
                    <h5 className="mb-1">{type.name || "-"}</h5>
                    <div className="text-muted mb-2">
                      Type ID: <code>{id}</code>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="secondary">{type.key || "-"}</Badge>
                      <Badge bg={isActive ? "success" : "secondary"}>
                        {type.status || "-"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <DetailRow label="Name" value={type.name || "-"} />
                  <DetailRow label="Key" value={type.key || "-"} />
                  <DetailRow label="Status" value={type.status || "-"} />
                </div>
                <div className="col-md-6">
                  <DetailRow
                    label="Created"
                    value={formatDate(type.created_at || type.createdAt)}
                  />
                  <DetailRow
                    label="Updated"
                    value={formatDate(type.updated_at || type.updatedAt)}
                  />
                </div>
              </div>

              <div className="mb-0">
                <div className="text-muted small mb-1">Description</div>
                <div>{type.description || "-"}</div>
              </div>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={goBack}>
              Back
            </Button>
            {canManage ? (
              <Button variant="primary" onClick={goEdit}>
                Edit Type
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypeViewPage;
